/**
 * Gráfico Donut (rosca) para semáforo AVCB/Alvará
 */
import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';

interface DonutData {
  regular: number;
  emAndamento: number;
  irregular: number;
}

interface DonutFarolChartProps {
  title: string;
  data: DonutData;
  isLoading?: boolean;
  className?: string;
  onSliceClick?: (category: 'regular' | 'emAndamento' | 'irregular') => void;
}

const COLORS = {
  regular: '#10b981', // emerald-500
  emAndamento: '#f59e0b', // amber-500
  irregular: '#ef4444', // red-500
};

const LABELS = {
  regular: 'Regular',
  emAndamento: 'Em Andamento',
  irregular: 'Irregular',
};

export function DonutFarolChart({
  title,
  data,
  isLoading = false,
  className,
  onSliceClick,
}: DonutFarolChartProps) {
  const total = (data?.regular || 0) + (data?.emAndamento || 0) + (data?.irregular || 0);
  const safeData = {
    regular: data?.regular || 0,
    emAndamento: data?.emAndamento || 0,
    irregular: data?.irregular || 0,
  };

  const option: EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const percent = ((params.value / total) * 100).toFixed(1);
        return `${params.name}: ${params.value.toLocaleString('pt-BR')} (${percent}%)`;
      },
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: title,
        type: 'pie',
        radius: ['50%', '75%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 4,
          borderColor: 'transparent',
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          scale: true,
          scaleSize: 8,
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
            formatter: (params: any) => {
              const percent = ((params.value / total) * 100).toFixed(0);
              return `${percent}%`;
            },
          },
        },
        data: total > 0 ? [
          { 
            value: safeData.regular, 
            name: LABELS.regular, 
            itemStyle: { color: COLORS.regular } 
          },
          { 
            value: safeData.emAndamento, 
            name: LABELS.emAndamento, 
            itemStyle: { color: COLORS.emAndamento } 
          },
          { 
            value: safeData.irregular, 
            name: LABELS.irregular, 
            itemStyle: { color: COLORS.irregular } 
          },
        ].filter(item => item.value > 0) : [
          { value: 1, name: 'Sem dados', itemStyle: { color: '#e5e7eb' } }
        ],
      },
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '42%',
        style: {
          text: total.toLocaleString('pt-BR'),
          fontSize: 24,
          fontWeight: 'bold',
          fill: '#888',
        },
      },
      {
        type: 'text',
        left: 'center',
        top: '55%',
        style: {
          text: 'Total',
          fontSize: 12,
          fill: '#888',
        },
      },
    ],
  };

  const handleChartClick = (params: any) => {
    if (!onSliceClick) return;
    
    const nameToCategory: Record<string, 'regular' | 'emAndamento' | 'irregular'> = {
      [LABELS.regular]: 'regular',
      [LABELS.emAndamento]: 'emAndamento',
      [LABELS.irregular]: 'irregular',
    };
    
    const category = nameToCategory[params.name];
    if (category) {
      onSliceClick(category);
    }
  };

  if (isLoading) {
    return (
      <div className={cn(
        'bg-card rounded-lg border p-4 animate-pulse',
        className
      )}>
        <div className="h-4 bg-muted rounded w-20 mb-4" />
        <div className="aspect-square max-w-[200px] mx-auto bg-muted rounded-full" />
      </div>
    );
  }

  return (
    <div className={cn(
      'bg-card rounded-lg border p-4 flex flex-col',
      className
    )}>
      <h3 className="text-sm font-medium text-muted-foreground mb-2">
        {title}
      </h3>
      
      <div className="flex-1 min-h-0">
        <ReactECharts
          option={option}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          onEvents={{
            click: handleChartClick,
          }}
        />
      </div>

      {/* Legenda customizada */}
      <div className="flex justify-center gap-4 mt-2">
        {[
          { key: 'regular', label: 'Regular', color: COLORS.regular, value: safeData.regular },
          { key: 'emAndamento', label: 'Andamento', color: COLORS.emAndamento, value: safeData.emAndamento },
          { key: 'irregular', label: 'Irregular', color: COLORS.irregular, value: safeData.irregular },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => onSliceClick?.(item.key as any)}
            disabled={item.value === 0}
            className={cn(
              'flex items-center gap-1 text-xs transition-all',
              item.value > 0 ? 'hover:opacity-80 cursor-pointer' : 'opacity-50 cursor-not-allowed'
            )}
            title={`${item.value.toLocaleString('pt-BR')} unidades`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-muted-foreground">
              {item.label} ({item.value.toLocaleString('pt-BR')})
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
